import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "react-query";
import { message } from "antd";
import ContactService from "services/ContactService";
import unpackAndShowErrors from "helpers/djangoErrorUnapacker";

const CONTACTS_KEY = "ALL_CONTACTS";
const CONTACTS_TRANSACTIONS = "CONTACTS_TRANSACTIONS";
const GET_CONTACTS = "GET_CONTACTS";
const GET_TAGS = "GET_TAGS";
export const GET_CONTACT = "GET_CONTACT";
const GET_CONTACT_BENEFITS = "GET_CONTACT_BENEFITS";
const GET_CONTACT_LOYALTY_PROGRAM_STATS = "GET_CONTACT_LOYALTY_PROGRAM_STATS";
export const GET_CONTACT_PAYMENTS_STATS = "GET_CONTACT_PAYMENTS_STATS";
export const GET_CONTACT_BILLING_TRANSACTIONS = "GET_CONTACT_BILLING_TRANSACTIONS";
export const INFINITE_CONTACTS = "INFINITE_CONTACTS";

export const useInfiniteContacts = ({ search = "", pageSize = 15 } = {}) =>
  useInfiniteQuery({
    queryKey: [INFINITE_CONTACTS, search, pageSize],
    queryFn: ({ pageParam = 1 }) =>
      ContactService.getContacts(pageParam, pageSize, search),
    getNextPageParam: (lastPage) => {
      if (lastPage?.data?.next) return lastPage.data.current + 1;
      return undefined;
    },
    keepPreviousData: true,
  });

const useGetAllContacts = () =>
  useQuery({
    queryKey: [CONTACTS_KEY],
    queryFn: () => ContactService.getAllContactsPaginationNone(),
    keepPreviousData: true,
  });

const useSendEmailInBulk = (successMsg) => {
  return useMutation({
    mutationFn: (payload) => {
      return ContactService.sendBulkEmail(payload);
    },
    onSuccess: () => {
      message.success(successMsg, 3);
    },
  });
};

export const useCheckContactsDelete = () => {
  return useMutation({
    mutationFn: (values) => ContactService.checkContactsDelete(values),
    onError: (error) => {
      if (error?.response?.data) unpackAndShowErrors(error.response.data);
    },
  });
};

const useSendSmsInBulk = (successMsg) => {
  return useMutation({
    mutationFn: (payload) => {
      return ContactService.sendBulkSMS(payload);
    },
    onSuccess: () => {
      message.success(successMsg, 3);
    },
  });
};

const useGetContactTransactions = (query, contactId) =>
  useQuery({
    queryKey: [CONTACTS_TRANSACTIONS, query],
    queryFn: () => ContactService.getContactTransactions(query, contactId),
    keepPreviousData: true,
  });

const useGetContacts = (payload) => {
  const { page, perPage, search } = payload;
  return useQuery({
    queryKey: [GET_CONTACTS, payload],
    queryFn: () => ContactService.getContacts(page, perPage, search),
    keepPreviousData: true,
  });
};

const useGetContact = (id, isEnabled = false) => {
  return useQuery({
    queryKey: [GET_CONTACT, id],
    queryFn: () => ContactService.getContact(id),
    keepPreviousData: true,
    enabled: isEnabled,
  });
};

export const useChangeContactImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return ContactService.changeImage(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_CONTACT);
    },
  });
};

export const useUpdateContactCard = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => {
      const contactCardInfo = { ...payload?.cardData, ...payload?.contactData };
      return ContactService.updateContactCard(id, contactCardInfo);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_CONTACT);
      onSuccess?.();
    },
    onError: (error) => {
      unpackAndShowErrors(error.response.data);
    },
  });
};

export const useUpdateContactStatus = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return ContactService.updateContactStatus(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_CONTACT);
      onSuccess?.();
    },
    onError: (error) => {
      unpackAndShowErrors(error.response.data);
    },
  });
};

export const useGetTags = () => {
  return useQuery({
    queryKey: [GET_TAGS],
    queryFn: () => ContactService.getTags(),
  });
};

export const useUpdateContactTags = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      const { id, data } = payload;
      return ContactService.editContact(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_CONTACT);
      onSuccess?.();
    },
    onError: (error) => {
      unpackAndShowErrors(error.response.data);
    },
  });
};

export const useUpdateContactAddress = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => {
      return ContactService.updateContactAddress(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_CONTACT);
      onSuccess?.();
    },
    onError: (error) => {
      unpackAndShowErrors(error.response.data);
    },
  });
};

export const useCreateContactAddress = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => {
      return ContactService.createContactAddress(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_CONTACT);
      onSuccess?.();
    },
    onError: (error) => {
      unpackAndShowErrors(error.response.data);
    },
  });
};

export const useUnsubscribeContact = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload, isPublicUnsubscribe) => {
      return ContactService.unsubscribeContact(payload, isPublicUnsubscribe);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_CONTACT);
      onSuccess && onSuccess();
    },
    onError: (error) => {
      unpackAndShowErrors(error.response.data);
    },
  });
};

export const useGetContactBenefits = (contactId) => {
  return useQuery({
    queryKey: [GET_CONTACT_BENEFITS, contactId],
    queryFn: () => ContactService.getContactBenefits(contactId),
  });
};

export const useGetContactLoyaltyStats = (contactId) => {
  return useQuery({
    queryKey: [GET_CONTACT_LOYALTY_PROGRAM_STATS, contactId],
    queryFn: () => ContactService.getContactLoyaltyStats(contactId),
    enabled: !!contactId,
  });
};

export const useGetContactPaymentsStats = (contactId) => {
  return useQuery({
    queryKey: [GET_CONTACT_PAYMENTS_STATS, contactId],
    queryFn: () => ContactService.getContactPaymentsStats(contactId),
    enabled: !!contactId,
  });
};

export const useGetContactBillingTransactions = (payload) => {
  return useQuery({
    queryKey: [GET_CONTACT_BILLING_TRANSACTIONS, payload],
    queryFn: () => ContactService.getContactBillingTransactions(payload),
    keepPreviousData: true,
    enabled: !!payload?.contact,
  });
};

export {
  useGetAllContacts,
  useSendEmailInBulk,
  useSendSmsInBulk,
  useGetContactTransactions,
  useGetContacts,
  useGetContact,
};
